import type { ActiveTimer, Session, Settings } from '@timato/shared';
import { uuidv7 } from '@timato/shared';
import { afterSession, completesAt, elapsedSec, isBreak, nextMode, start } from './engine';

/** A run shorter than this is not worth a row when skipped or reset. */
export const MIN_LOGGED_SEC = 60;
/** Auto-start a break only if completion was noticed promptly; after a long sleep the user has clearly left. */
const AUTO_START_WINDOW_MS = 60_000;

type Ctx = { now: Date; settings: Settings; deviceId: string; labelIds: string[]; teamId?: string | null };
export type Outcome = { session: Session | null; next: ActiveTimer };

function sessionFromRun(t: ActiveTimer, endedAt: Date, durationSec: number, status: Session['status'], labelIds: string[], teamId: string | null = null): Session {
  const startedAt = t.startedAt ?? new Date(endedAt.getTime() - durationSec * 1000).toISOString();
  return {
    // The session id IS the run id, so two devices completing the same run write the same row (idempotent).
    id: t.runId ?? uuidv7(endedAt.getTime()),
    teamId: teamId ?? null, taskId: t.taskId, projectId: t.projectId,
    mode: t.mode, status, startedAt, endedAt: endedAt.toISOString(), durationSec: Math.round(durationSec),
    goalText: isBreak(t.mode) ? '' : t.goalText, labelIds: isBreak(t.mode) ? [] : labelIds,
    source: 'timer', editedAt: null, editedBy: null, updatedAt: '', deletedAt: null,
  };
}

/** A countdown reached zero. Logs the session, advances the cycle, and auto-starts a break when configured. */
export function completeCountdown(t: ActiveTimer, ctx: Ctx): Outcome {
  const endedAt = completesAt(t) ?? ctx.now;
  const session = sessionFromRun(t, endedAt, t.durationSec ?? elapsedSec(t, endedAt), isBreak(t.mode) ? 'break' : 'completed', ctx.labelIds, ctx.teamId ?? null);
  const n = nextMode(t, ctx.settings);
  let next: ActiveTimer = { ...afterSession(t), mode: n.mode, cycleCount: n.cycleCount };
  const promptly = ctx.now.getTime() - endedAt.getTime() < AUTO_START_WINDOW_MS;
  if (ctx.settings.autoStartBreaks && isBreak(n.mode) && promptly) next = start(next, { ...ctx, now: endedAt });
  return { session, next };
}

/** Skip the current countdown: log what happened if it was long enough, move to the next mode, idle. */
export function skipCountdown(t: ActiveTimer, ctx: Ctx): Outcome {
  const elapsed = elapsedSec(t, ctx.now);
  const session = t.status !== 'idle' && elapsed >= MIN_LOGGED_SEC
    ? sessionFromRun(t, ctx.now, elapsed, isBreak(t.mode) ? 'break' : 'skipped', ctx.labelIds, ctx.teamId ?? null) : null;
  const n = nextMode(t, ctx.settings);
  return { session, next: { ...afterSession(t), mode: n.mode, cycleCount: n.cycleCount } };
}

/** Finish a Track run: log the elapsed time as completed, stay in Track, idle. */
export function finishTrack(t: ActiveTimer, ctx: Ctx): Outcome {
  const elapsed = elapsedSec(t, ctx.now);
  const session = t.status !== 'idle' && elapsed >= 1 ? sessionFromRun(t, ctx.now, elapsed, 'completed', ctx.labelIds, ctx.teamId ?? null) : null;
  return { session, next: afterSession(t) };
}

/**
 * End whatever runs so another can start in its place (TW-27). The time so far is logged the way Finish (Track) or
 * Skip (a countdown) would log it, but the cycle stays where it was: this run was interrupted, not completed.
 */
export function endForRestart(t: ActiveTimer, ctx: Ctx): Outcome {
  if (t.mode === 'track') return finishTrack(t, ctx);
  return { session: skipCountdown(t, ctx).session, next: afterSession(t) };
}

/**
 * Park a Track run that is switched away from (TW-32): its time so far is saved under the run's id as `parked`, so it
 * counts and shows in Sessions, and switching back resumes the same row rather than adding one per switch.
 */
export function parkTrack(t: ActiveTimer, ctx: Ctx): Outcome {
  const { session, next } = finishTrack(t, ctx);
  return { session: session && { ...session, status: 'parked' }, next };
}

/**
 * The run a parked session continues (TW-32): its id, start and time so far, running again from `now` on top of `t`
 * (idle, goal and labels already set). Ending it later writes the same row, now with the whole time.
 */
export function resumeParked(t: ActiveTimer, parked: Session, { now, deviceId }: { now: Date; deviceId: string }): ActiveTimer {
  return {
    ...t, runId: parked.id, status: 'running', mode: 'track', startedAt: parked.startedAt, durationSec: null,
    accumSec: parked.durationSec, anchorAt: now.toISOString(), idleSegments: [], startedOnDeviceId: deviceId,
  };
}
